import { generateToken, getShouldRefreshToken, pureRefreshToken, reLogin } from '@dmp/utils';
import { getBeApiDomain } from '@dmp/utils/tenant';
import axios, { AxiosError } from 'axios';

export const baseURL = getBeApiDomain();

export const dataMarketplaceService = axios.create({
  baseURL,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
});

dataMarketplaceService.interceptors.request.use((config) => {
  config.headers['Authorization'] = generateToken();
  return config;
});

/** Separate, interceptor-free client for the refresh-token call itself (see
 * `requestRefreshToken` in api.ts). It must never go through `dataMarketplaceService`'s own
 * 401-triggers-refresh interceptor below — if the refresh call's own response is a 401 (the
 * refresh token itself is expired/invalid), that would re-enter `refreshTokenRequest()` while
 * the very refresh it's part of is still in flight, awaiting a promise that can never resolve
 * (the app hangs instead of returning to the login page). */
export const refreshTokenService = axios.create({
  baseURL,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
});

let refreshTokenRequestPromise: Promise<void> | null = null;

const refreshTokenRequest = async () => {
  if (refreshTokenRequestPromise) return refreshTokenRequestPromise;
  refreshTokenRequestPromise = pureRefreshToken();
  try {
    const requestRes = await refreshTokenRequestPromise;
    return requestRes;
  } finally {
    refreshTokenRequestPromise = null;
  }
};

dataMarketplaceService.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    if (error.response?.status === 401) {
      const shouldRefreshToken = getShouldRefreshToken();

      if (!shouldRefreshToken) {
        // reLogin();
        // do nothing , dmp backend is messed up
      } else {
        try {
          await refreshTokenRequest();
          // retry the request through dataMarketplaceService (not the bare axios instance) so its
          // request interceptor re-attaches the freshly-refreshed token instead of resending the
          // expired one still baked into error.config
          try {
            const retryRes = await dataMarketplaceService.request(error.config!);
            return retryRes;
          } catch (retryError) {
            // after refresh token, still get 401, reLogin
            if ((retryError as AxiosError).response?.status === 401) {
              reLogin();
            }
          }
        } catch {
          // refresh token failed, reLogin
          reLogin();
        }
      }
    }

    return Promise.reject(error);
  }
);
